import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { authedFetch } from "../lib/api";
import { resolveRole } from "../lib/roles";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Ensure a matching gp_users row exists for the authenticated user.
// Called after we have a real session (auth.uid() available for RLS).
const userRecordPromises = new Map();
const userRecordsReady = new Set();

async function ensureUserRecord(user) {
  if (!user) return;
  if (userRecordsReady.has(user.id)) return;
  const current = userRecordPromises.get(user.id);
  if (current) return current;
  const promise = ensureUserRecordOnce(user)
    .then(() => userRecordsReady.add(user.id))
    .catch((error) => console.error("User record provisioning failed:", error))
    .finally(() => userRecordPromises.delete(user.id));
  userRecordPromises.set(user.id, promise);
  return promise;
}

async function ensureUserRecordOnce(user) {
  if (!user) return;

  const role = user.user_metadata?.role;
  const m = user.user_metadata || {};

  // Email-confirmed clinician signups may receive their first real session only
  // after returning from the verification email. Complete onboarding here so the
  // AHPRA queue and subscription record are never skipped.
  if (role === "clinician") {
    const { data: existingClinician } = await supabase.from("clinicians").select("id").eq("email", user.email).maybeSingle();
    if (!existingClinician) {
      await authedFetch("/onboard/clinician", {
        method: "POST",
        body: JSON.stringify({
          first_name: m.first_name || "",
          last_name: m.last_name || "",
          profession: m.profession || "PSYCHOLOGIST",
          specialty: m.specialty || "",
          appointment_types: m.appointment_types || [],
          suburb: m.suburb || "",
          state: m.state || "",
          postcode: m.postcode || "",
          phone: m.phone || "",
          ahpra_number: m.ahpra_number || "",
          gender: m.gender || null,
          languages: m.languages || [],
          photo_url: m.photo_url || null,
        }),
      });
    }
    return;
  }

  // Clinic manager onboarding is completed by the clinic portal.
  if (role && role !== "gp") return;

  // If this auth account is a clinician (by email), don't create a GP record.
  const { data: clin } = await supabase
    .from("clinicians")
    .select("id")
    .eq("email", user.email)
    .maybeSingle();
  if (clin) return;

  // If this auth account is a clinic admin (accounts.billing_email), skip too.
  const { data: acct } = await supabase
    .from("accounts")
    .select("id")
    .eq("billing_email", user.email)
    .maybeSingle();
  if (acct) return;

  const { data: existing, error } = await supabase
    .from("gp_users")
    .select("id")
    .eq("auth_user_id", user.id)
    .maybeSingle();

  if (error) {
    console.error("gp_users lookup error:", error.message);
    return;
  }
  if (existing) return;

  const { error: insertError } = await supabase.from("gp_users").insert({
    auth_user_id: user.id,
    email: user.email,
    first_name: m.first_name || "",
    last_name: m.last_name || "",
    clinic_name: m.clinic_name || "",
    suburb: m.suburb || "",
  });
  if (insertError) { console.error("gp_users insert error:", insertError.message); return; }
  // New GP — send a one-time welcome email (fire-and-forget).
  authedFetch("/notify/welcome", { method: "POST", body: JSON.stringify({ role: "gp" }) }).catch(() => {});
}

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);

  const handleUser = useCallback(async (u) => {
    setUser(u ?? null);
    if (!u) { setRole(null); setRoleLoading(false); return; }
    setRoleLoading(true);
    await ensureUserRecord(u);
    try {
      setRole(await resolveRole(u));
    } catch {
      setRole("gp");
    } finally {
      setRoleLoading(false);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      handleUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      handleUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [handleUser]);

  const signUp = useCallback(async ({ email, password, firstName, lastName, clinicName, suburb, role, profession, specialty, state, postcode, phone, ahpraNumber, gender, languages, appointmentTypes, photoUrl }) => {
    return supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
          clinic_name: clinicName,
          suburb,
          role: role || "gp",
          profession,
          specialty,
          state,
          postcode,
          phone,
          ahpra_number: ahpraNumber,
          gender,
          languages,
          appointment_types: appointmentTypes || [],
          photo_url: photoUrl,
        },
        emailRedirectTo: `${window.location.origin}/login`,
      },
    });
  }, []);

  const signIn = useCallback(async ({ email, password }) => {
    return supabase.auth.signInWithPassword({ email, password });
  }, []);

  const signInWithProvider = useCallback(async (provider) => {
    const options = { redirectTo: `${window.location.origin}/auth/callback` };
    if (provider === "azure") options.scopes = "openid email profile";
    return supabase.auth.signInWithOAuth({ provider, options });
  }, []);

  const resetPassword = useCallback(async (email) => {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
  }, []);

  const updatePassword = useCallback(async (password) => {
    return supabase.auth.updateUser({ password });
  }, []);

  const signOut = useCallback(async () => {
    return supabase.auth.signOut();
  }, []);

  const value = { session, user, loading, role, roleLoading, signUp, signIn, signInWithProvider, resetPassword, updatePassword, signOut };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
